import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { ProjectsFilter } from '../../../../aumico-lib-projects-fe/src/lib/interfaces/projects-filter.interface';
import { MfaFilter } from '../../../../aumico-lib-users-fe/src/lib/interfaces/mfa-filter.interface';
import {
    clearCompanySpecificProjectsFilter,
    clearDoNotShowAgainMessages,
    clearMfaFilter,
    clearProjectsFilter,
    clearScrollToText,
    clearTasksFilter,
    setAccordionOpenState,
    setCompanySpecificProjectsFilter,
    setDoNotShowAgainMessage,
    setMfaFilter,
    setProjectNavigationContext,
    setProjectsFilter,
    setScrollToTextState,
    setSelectedTabState,
    setTasksFilter,
    unsetDoNotShowAgainMessage,
} from '../actions/ui.actions';
import { CPMM_UI_FEATURE } from '../constants/constants';
import { TasksFilter } from '../interfaces/tasks-filter.interface';

export interface ProjectNavigationContext {
    context: 'summary' | 'general' | 'report';
}

export interface UiState {
    accordionOpenState: { [locationId: string]: { [sectionId: string]: boolean } };
    selectedTabState: { [locationId: string]: string };
    scrollToTextState: string | null;
    projectNavigationContext: { context: 'my-projects' | 'all-projects' };
    doNotShowAgain: { [messageId: string]: boolean };
    tasksFilter?: TasksFilter;
    projectsFilter?: ProjectsFilter;
    companySpecificProjectsFilter?: ProjectsFilter; // Added for company-specific projects filter
    mfaFilter?: MfaFilter; // Sorting state for the 2FA users table
}

export const initialUiState: UiState = {
    accordionOpenState: {},
    selectedTabState: {},
    scrollToTextState: null,
    projectNavigationContext: { context: 'my-projects' },
    doNotShowAgain: {},
    tasksFilter: {}, // Initialize tasksFilter as an empty object
    projectsFilter: {}, // Initialize projectsFilter as an empty object
    companySpecificProjectsFilter: {}, // Initialize companySpecificProjectsFilter as an empty object
    mfaFilter: {}, // Initialize mfaFilter as an empty object
};

const _uiReducer = createReducer(
    initialUiState,
    on(setAccordionOpenState, (state, { locationId, sectionId, isOpen }) => ({
        ...state,
        accordionOpenState: {
            ...state.accordionOpenState,
            [locationId]: {
                ...state.accordionOpenState[locationId],
                [sectionId]: isOpen,
            },
        },
    })),
    on(setSelectedTabState, (state, { locationId, selectedTabKey }) => ({
        ...state,
        selectedTabState: {
            ...state.selectedTabState,
            [locationId]: selectedTabKey,
        },
    })),
    on(setScrollToTextState, (state, { text }) => ({
        ...state,
        scrollToTextState: text,
    })),
    on(clearScrollToText, (state) => ({
        ...state,
        scrollToTextState: null,
    })),
    on(setProjectNavigationContext, (state, { context }) => {
        return {
            ...state,
            projectNavigationContext: { context },
        };
    }),
    on(setDoNotShowAgainMessage, (state, { messageId, value }) => ({
        ...state,
        doNotShowAgain: {
            ...state.doNotShowAgain,
            [messageId]: value,
        },
    })),
    on(unsetDoNotShowAgainMessage, (state, { messageId }) => {
        // Check if messageId ends with wildcard *
        const isWildcard = messageId.endsWith('*');

        return {
            ...state,
            doNotShowAgain: Object.keys(state.doNotShowAgain || {}).reduce((acc, key) => {
                if (isWildcard) {
                    // Remove the * and check if key starts with the pattern
                    const pattern = messageId.slice(0, -1); // Remove the trailing *
                    if (!key.startsWith(pattern)) {
                        acc[key] = state.doNotShowAgain[key];
                    }
                    // If key starts with pattern, don't add it to acc (effectively removing it)
                } else {
                    // Exact match - only remove if key exactly matches messageId
                    if (key !== messageId) {
                        acc[key] = state.doNotShowAgain[key];
                    }
                }
                return acc;
            }, {} as { [messageId: string]: boolean }),
        };
    }),
    on(clearDoNotShowAgainMessages, (state) => ({
        ...state,
        doNotShowAgain: {},
    })),
    on(setTasksFilter, (state, { filter }) => ({
        ...state,
        tasksFilter: {
            ...state.tasksFilter,
            ...filter,
        },
    })),
    on(clearTasksFilter, (state) => ({
        ...state,
        tasksFilter: {}, // Reset tasksFilter to an empty object
    })),
    on(setProjectsFilter, (state, { filter }) => ({
        ...state,
        projectsFilter: {
            ...state.projectsFilter,
            ...filter,
        },
    })),
    on(clearProjectsFilter, (state) => ({
        ...state,
        projectsFilter: {}, // Reset projectsFilter to an empty object
    })),
    on(setCompanySpecificProjectsFilter, (state, { filter }) => ({
        ...state,
        companySpecificProjectsFilter: {
            ...state.companySpecificProjectsFilter,
            ...filter,
        },
    })),
    on(clearCompanySpecificProjectsFilter, (state) => ({
        ...state,
        companySpecificProjectsFilter: {}, // Reset companySpecificProjectsFilter to an empty object
    })),
    on(setMfaFilter, (state, { filter }) => ({
        ...state,
        mfaFilter: {
            ...state.mfaFilter,
            ...filter,
        },
    })),
    on(clearMfaFilter, (state) => ({
        ...state,
        mfaFilter: {}, // Reset mfaFilter to an empty object
    })),
);

export function uiReducer(state: UiState | undefined, action: Action) {
    return _uiReducer(state, action);
}

export const selectUiState = createFeatureSelector<UiState>(CPMM_UI_FEATURE);
export const selectAccordionOpenState = (locationId: string) =>
    createSelector(selectUiState, (state) => state.accordionOpenState[locationId] || {});
export const selectSelectedTabState = (locationId: string) =>
    createSelector(selectUiState, (state) => state.selectedTabState[locationId] || null);
export const selectScrollToTextState = () => createSelector(selectUiState, (state) => state.scrollToTextState || null);
export const selectProjectNavigationContext = () =>
    createSelector(selectUiState, (state) => state.projectNavigationContext || { context: 'my-projects' });
export const selectDoNotShowAgainMessagesStates = () =>
    createSelector(selectUiState, (state): { [messageId: string]: boolean } => state.doNotShowAgain || {});

export const selectDoNotShowAgainMessageState = (messageId: string) =>
    createSelector(selectUiState, (state): boolean => !!state.doNotShowAgain?.[messageId]);
export const selectTasksFilter = () => createSelector(selectUiState, (state): TasksFilter => state.tasksFilter || {});
export const selectProjectsFilter = () =>
    createSelector(selectUiState, (state): ProjectsFilter => state.projectsFilter || {});

export const selectCompanySpecificProjectsFilter = () =>
    createSelector(selectUiState, (state): ProjectsFilter => state.companySpecificProjectsFilter || {});

export const selectMfaFilter = () =>
    createSelector(selectUiState, (state): MfaFilter => state.mfaFilter || {});
